import React, { useState } from 'react';
import { Box, Stack, Typography, Divider, Skeleton, Avatar, TextField, Button, Badge } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PropsWithLoadingState } from '../../../utilities';
import { getRelativeDateString } from '../../../utilities/time';
import { useIsMounted } from '../../../hooks/useIsMounted';

export interface IProfileChatSectionProps {
    /** The user info, interlocuteur to chat with */
    user: {
        avatar: string;
        displayName: string;
        isOnline: boolean;
        lastOnline: Date;
    }
    /** Optional pre-filled message */
    prefilledMessage?: string;
    /** Callback on message sent button click */
    onMessageSentClick: (message: string) => Promise<void>
}

export const ProfileChatSection = ({ user, prefilledMessage, onMessageSentClick, loading }: PropsWithLoadingState<IProfileChatSectionProps>) => {
    const { t, i18n } = useTranslation();
    const [message, setMessage] = useState(prefilledMessage || '');
    const [sending, setSending] = useState(false);
    const isMounted = useIsMounted()

    const handleSubmitClick = async () => {
        setSending(true);
        await onMessageSentClick?.(message);
        if (isMounted()) {
            //if the component is still mounted after the message sent: we clean the states
            setMessage('');
            setSending(false);
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Divider>{!loading ? t('user.profile.chatSectionTitle', { name: user.displayName }) : ''}</Divider>
            <Stack direction={'column'} spacing={2}>
                {/* Reminder of profile information (which could be far up the page) */}
                <Stack direction={'row'} spacing={1} justifyContent={'start'} alignItems={'center'}>
                    {!loading ?
                        !!user.isOnline ?
                            <Badge
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                            >
                                <Avatar src={user.avatar} />
                            </Badge>
                            : <Avatar src={user.avatar} />
                        : <Skeleton variant="circular">
                            <Avatar />
                        </Skeleton>}
                    <Stack direction={'column'} justifyContent={'space-evenly'}>
                        <Typography fontWeight={'bold'}>{!loading ? user.displayName : <Skeleton />}</Typography>
                        <Typography variant='caption'>{!loading ? t('user.profile.chatUserLastSeen', { date: getRelativeDateString(user.lastOnline, i18n.language) }) : <Skeleton />}</Typography>
                    </Stack>
                </Stack>
                {/* The text input for the message (for the profile, we keep it simple: only text) */}
                <TextField
                    label={t('user.profile.chatSectionInputLabel')}
                    placeholder={t('user.profile.chatSectionInputPlaceholder')}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={!!loading || sending}
                    multiline
                    minRows={3}
                    maxRows={5}
                />
                {/* Send button */}
                <Button onClick={handleSubmitClick}
                    disabled={!!loading || sending}
                    variant='outlined'
                >{t('user.profile.chatSectionButton', { context: sending ? 'sending' : 'idle' })}</Button>
            </Stack>
        </Box>
    );
}
